/**
 * ✅ MODERN Vue 3 Composable: Product Pricing Management
 *
 * Central composable for managing single product pricing calculations.
 * Handles reactive state, auto-recalculation, and API communication.
 *
 * Usage:
 * ```ts
 * const {
 *   price,
 *   isLoading,
 *   configure,
 *   reset
 * } = useProductPricing()
 *
 * configure({
 *   productId: '123',
 *   quantity: 2,
 *   selectedSizeId: 'size-small',
 *   extraIngredients: [{ id: '1', name: 'Cheese', priceCent: 100 }]
 * })
 * ```
 */
import { ref, computed, inject } from 'vue';
import eventBus from '@shared/utils/eventBus';
// Track composable instances to debug singleton vs. per-modal usage
let instanceCounter = 0;
/**
 * ✅ CENTRAL Composable: useProductPricing
 *
 * Manages reactive product pricing with auto-recalculation
 * and debounced API calls.
 */
export function useProductPricing() {
    // Keep timers instance-local to avoid cross-modal race conditions.
    let debounceTimer = null;
    let activeRequestTimeout = null;
    let activeRequestId = 0;
    // ✅ Inject API Client (provided by app)
    const apiClient = inject('apiClient');
    if (!apiClient) {
        throw new Error('RestaurantApiClient not provided. Make sure to provide it in your app.');
    }
    const instanceId = ++instanceCounter;
    // ✅ Reactive State
    const state = ref({
        response: null,
        priceEuro: 0,
        priceCent: 0,
        isLoading: false,
        error: null,
        breakdown: null
    });
    // ✅ Current Configuration
    const config = ref(null);
    // ✅ Computed: Formatted Price (German locale)
    const formattedPrice = computed(() => {
        if (state.value.priceEuro === 0)
            return '0,00 €';
        return new Intl.NumberFormat('de-DE', {
            style: 'currency',
            currency: 'EUR'
        }).format(state.value.priceEuro);
    });
    // ✅ Computed: Has valid configuration
    const isConfigured = computed(() => {
        return config.value !== null &&
            config.value.productId.length > 0 &&
            config.value.quantity > 0;
    });
    /**
     * ✅ Calculate price from server (debounced)
     */
    const calculatePrice = async () => {
        if (!config.value || !isConfigured.value) {
            console.warn('⚠️ [useProductPricing] Cannot calculate: No valid configuration');
            return;
        }
        // Clear existing debounce timer
        if (debounceTimer) {
            clearTimeout(debounceTimer);
        }
        // Set loading immediately
        state.value.isLoading = true;
        // Track request lifecycle for timeout handling
        activeRequestId += 1;
        const requestId = activeRequestId;
        if (activeRequestTimeout) {
            clearTimeout(activeRequestTimeout);
            activeRequestTimeout = null;
        }
        activeRequestTimeout = setTimeout(() => {
            if (requestId !== activeRequestId) {
                return;
            }
            console.warn('⏱️ [useProductPricing] Pricing request timed out', {
                instanceId,
                requestId,
                config: config.value
            });
            state.value.isLoading = false;
            state.value.error = 'Preisberechnung dauert ungewöhnlich lange';
            if (config.value?.channelId) {
                const timeoutDetail = {
                    channelId: config.value.channelId,
                    projectId: config.value.projectId,
                    productId: config.value.productId,
                    result: {
                        price: state.value.priceEuro,
                        priceCent: state.value.priceCent,
                        formattedPrice: formattedPrice.value,
                        isLoading: false,
                        error: state.value.error ?? 'Preisberechnung Timeout',
                        breakdown: state.value.breakdown ?? undefined,
                        response: state.value.response ?? undefined
                    }
                };
                const timeoutLoadingDetail = {
                    channelId: config.value.channelId,
                    projectId: config.value.projectId,
                    productId: config.value.productId,
                    isLoading: false
                };
                eventBus.emit('productPricing:result', timeoutDetail);
                eventBus.emit('productPricing:loading', timeoutLoadingDetail);
            }
        }, 5000);
        // Debounce API call (300ms)
        debounceTimer = setTimeout(async () => {
            try {
                // Build API request
                const request = {
                    productId: config.value.productId,
                    quantity: config.value.quantity,
                    selectedSizeId: config.value.selectedSizeId,
                    extraIngredients: config.value.extraIngredients?.map(extra => ({
                        id: extra.id,
                        name: extra.name,
                        price: extra.price,
                        priceCent: extra.priceCent || extra.price * 100
                    })),
                    removedIngredients: config.value.removedIngredients
                };
                // Call API
                const response = await apiClient.calculateProductPrice(request);
                if (response.status === 'success' && response.data?.pricing) {
                    if (requestId === activeRequestId && activeRequestTimeout) {
                        clearTimeout(activeRequestTimeout);
                        activeRequestTimeout = null;
                    }
                    const pricing = response.data.pricing;
                    // Update state
                    state.value = {
                        response,
                        priceEuro: pricing.lineItemTotalCent / 100,
                        priceCent: pricing.lineItemTotalCent,
                        isLoading: false,
                        error: null,
                        breakdown: pricing
                    };
                    const snapshot = {
                        instanceId,
                        timestamp: new Date().toISOString(),
                        state: { ...state.value },
                        formattedPrice: formattedPrice.value
                    };
                    globalThis.__debugProductPricingLastState = snapshot;
                    if (config.value?.channelId) {
                        const resultDetail = {
                            channelId: config.value.channelId,
                            projectId: config.value.projectId,
                            productId: config.value.productId,
                            result: {
                                price: state.value.priceEuro,
                                priceCent: state.value.priceCent,
                                formattedPrice: formattedPrice.value,
                                isLoading: state.value.isLoading,
                                breakdown: state.value.breakdown ?? undefined,
                                response: state.value.response ?? undefined
                            }
                        };
                        const loadingDetail = {
                            channelId: config.value.channelId,
                            projectId: config.value.projectId,
                            productId: config.value.productId,
                            isLoading: state.value.isLoading
                        };
                        eventBus.emit('productPricing:result', resultDetail);
                        eventBus.emit('productPricing:loading', loadingDetail);
                    }
                }
                else {
                    throw new Error('Invalid response from server');
                }
            }
            catch (error) {
                console.error('❌ [useProductPricing] Calculation failed:', error);
                state.value.isLoading = false;
                state.value.error = error?.message || 'Preisberechnung fehlgeschlagen';
                state.value.breakdown = null;
                state.value.response = null;
                if (requestId === activeRequestId && activeRequestTimeout) {
                    clearTimeout(activeRequestTimeout);
                    activeRequestTimeout = null;
                }
                if (config.value?.channelId) {
                    const errorMessage = state.value.error ?? undefined;
                    const resultDetail = {
                        channelId: config.value.channelId,
                        projectId: config.value.projectId,
                        productId: config.value.productId,
                        result: {
                            price: 0,
                            priceCent: 0,
                            formattedPrice: '—',
                            isLoading: false,
                            error: errorMessage,
                            response: state.value.response ?? undefined,
                            breakdown: state.value.breakdown ?? undefined
                        }
                    };
                    const loadingDetail = {
                        channelId: config.value.channelId,
                        projectId: config.value.projectId,
                        productId: config.value.productId,
                        isLoading: false
                    };
                    eventBus.emit('productPricing:result', resultDetail);
                    eventBus.emit('productPricing:loading', loadingDetail);
                }
            }
        }, 300); // 300ms debounce
    };
    /**
     * ✅ Configure product and trigger calculation
     */
    const configure = (newConfig) => {
        config.value = { ...newConfig };
    };
    /**
     * ✅ Update specific config property (convenience method)
     */
    const updateConfig = (updates) => {
        if (!config.value) {
            console.warn('⚠️ [useProductPricing] Cannot update: No config set');
            return;
        }
        config.value = {
            ...config.value,
            ...updates
        };
    };
    /**
     * ✅ Reset state to initial values
     */
    const reset = () => {
        config.value = null;
        state.value = {
            response: null,
            priceEuro: 0,
            priceCent: 0,
            isLoading: false,
            error: null,
            breakdown: null
        };
        // Clear debounce timer
        if (debounceTimer) {
            clearTimeout(debounceTimer);
            debounceTimer = null;
        }
        if (activeRequestTimeout) {
            clearTimeout(activeRequestTimeout);
            activeRequestTimeout = null;
        }
    };
    // ✅ Auto-recalculate watch removed:
    // calculatePrice() is now called explicitly by the caller after
    // configure()/updateConfig(). Removing the watcher prevents double
    // calculation cycles and the resulting reactive races during
    // checkbox toggles inside the product config modal.
    return {
        instanceId,
        // State (read-only via computed)
        price: computed(() => state.value.priceEuro),
        priceCent: computed(() => state.value.priceCent),
        formattedPrice,
        isLoading: computed(() => state.value.isLoading),
        error: computed(() => state.value.error),
        breakdown: computed(() => state.value.breakdown),
        response: computed(() => state.value.response),
        // Configuration
        config: computed(() => config.value),
        isConfigured,
        // Actions
        configure,
        updateConfig,
        reset,
        calculatePrice // Manual trigger if needed
    };
}
